import type { RefObject } from 'react';
import type { AdBreakView, ChannelKind, ClientType, LiveView } from '@apm/contracts';
import type { LiveSwitchTarget } from '@apm/bloom-receiver-sdk';
import { purchaseLabelKey } from '../client-type.js';
import type { SkipButtonState } from '../useReceiver.js';
import { SEEK_STEP_SEC } from '@apm/bloom-receiver-sdk';
import { PlayerIcon } from './icons/PlayerIcons.js';
import { useT } from '../i18n/LocaleProvider.js';
import { AdPurchaseButton, AdSkipButton } from './AdControls.js';

/**
 * 컨트롤 바.
 *
 * **구매·정보보기는 재생을 멈추지 않는다** (CLAUDE.md 절대 규칙 7·14). 오버레이만
 * 토글한다 — 시청자가 채널을 이탈하지 않는 것이 이 시스템의 핵심 가치다.
 *
 * ── 문구가 곧 동작이다 ───────────────────────────────────────
 *
 * T8 이 `구매하기` → `모바일에서 구매` 로 바꿨다. 이 버튼이 하는 일이 "QR 을 띄운다" 가
 * 아니라 "구매를 손에 든 기기로 넘긴다" 가 됐기 때문이다.
 *
 * **B9 이 그것을 다시 갈랐다.** 폰으로 보고 있으면 넘길 곳이 없다 — 손에 든 기기가 곧
 * 그 기기이므로 「모바일에서 구매」는 거짓말이다. 그래서 `mobile_web` 에서만 `구매하기` 다.
 *
 * **응답을 기다렸다 문구를 바꾸지 않는다** (`docs/14` B9). 누른 뒤에 글자가 변하면
 * 시청자에게는 오류로 보인다 — 그래서 `clientType` 으로 **미리** 정한다. 서버의 Tier 0
 * 조건도 같은 값 하나(`clientType === 'mobile_web'`)라 판정이 갈리지 않는다.
 *
 * > **페이지는 어느 경우에도 이동하지 않는다** (절대 규칙 14, B9). v7 은 `same_device`
 * > 에서 떠났고 그 자리에 지금은 상품 시트가 열린다.
 *
 * ── 아이콘 버튼 (`docs/컨트롤바-아이콘-착수지시서.md`) ─────────────────
 *
 * 평소 바의 버튼(채널 · 홈 · ▲▼ · 전체 회차/상품 · 다음 회차/건너뛰기 · 탐색 셋)은 **아이콘만
 * 보인다.** 문구는 없애지 않고 두 곳으로 옮긴다 — `aria-label`(사전 키 · 절대 규칙 24)과, 포커스
 * 또는 마우스를 올렸을 때 버튼 위에 뜨는 **이름표**(CSS 가 `aria-label` 을 그대로 그린다 —
 * 문구가 두 벌이 되지 않는다). 보이는 글자는 **채널 번호**와 **건너뛰기의 남은 초** 둘뿐이다.
 *
 * **글자로 남는 것** — 구매·정보(홈쇼핑 · 범위 밖), 광고 중의 두 버튼, 「라이브 ↔ 이어보기」.
 * 셋 다 상황에 따라 잠깐 서는 버튼이라 글자가 그 상황을 말한다.
 *
 * **핸들러 · DOM 순서 · `data-testid` · `data-focus-*` 는 한 글자도 바꾸지 않았다** — 바뀐 것은
 * 버튼의 내용물과 스타일뿐이다.
 */
export function ControlBar({
  clientType,
  channelKind,
  channelName,
  channelNumber,
  onOpenChannels,
  onZapChannel,
  onOpenProducts,
  contentLevels = false,
  purchaseRef,
  allProductsRef,
  handoffOpen,
  handoffPending,
  infoOpen,
  skip,
  adBreak,
  live,
  liveSwitch,
  onSwitchLive,
  onRequestHandoff,
  onToggleInfo,
  onSkip,
  onHome,
  onSkipAd,
  onRequestAdHandoff,
  transport,
}: {
  /** 이번 진입의 재생 클라이언트 유형. **문구를 미리 정한다** */
  clientType: ClientType;
  /**
   * 채널의 종류 (v16). **일반 채널이면 구매·정보 버튼을 렌더하지 않는다.**
   *
   * `disabled` 로 두지 않는 이유는 포커스 링이 `button:not([disabled])` 로 후보를 모으기
   * 때문이 아니다 — 그것만이면 `disabled` 로 충분하다. `hidden` 이 아닌 `disabled` 버튼은
   * 여전히 `getClientRects()` 에 잡혀 **레이아웃 자리를 차지하고**, 폰 폭에서 남은 셋을
   * 밀어낸다. 렌더하지 않는 것이 유일하게 안전하다 (`docs/38` §9 의 3).
   *
   * 목록을 받기 전에는 `null` 이다 — 그때는 상거래 버튼을 그리지 않는다. 일반 채널에서
   * 잠깐 보였다 사라지는 것보다 늦게 나타나는 편이 낫다.
   */
  channelKind: ChannelKind | null;
  /** 지금 보는 채널의 이름. **등록된 값이라 번역하지 않는다** */
  channelName: string | null;
  /**
   * v26. 채널 번호 (절대 규칙 71). **이름 앞에 선다** — 이 버튼은 폭이 좁아 뒤가 잘리는데
   * (`.controls__button--channel` 의 `text-overflow`), 앞에 두면 **번호는 절대 잘리지
   * 않는다.** 그리고 숫자 키를 누르는 사람이 보는 값이 바로 그것이다.
   *
   * **버튼을 늘리지 않는다** — 컨트롤 바의 후보는 이미 일곱이다(`docs/51` §5.3-d).
   */
  channelNumber: number | null;
  /**
   * 채널 목록 시트를 연다. **`null` 이면 버튼 자체가 없다** — 공개 채널이 하나뿐이면
   * 고를 것이 없고, 없는 선택지는 눌러도 같은 화면으로 돌아온다.
   */
  onOpenChannels: (() => void) | null;
  /**
   * 채널 재핑 (v25). `null` 이면 그리지 않는다 — 채널이 하나뿐일 때다.
   *
   * **`disabled` 로 두지 않고 렌더하지 않는다** — 포커스 링이
   * `button:not([disabled])` 로 후보를 모으므로 `disabled` 여도 DOM 에 남아
   * **포커스가 빈 자리로 간다** (절대 규칙 29·45).
   */
  onZapChannel: ((step: 1 | -1) => void) | null;
  /**
   * 「전체 상품」 시트를 연다 (v11). **`null` 이면 버튼 자체가 없다** — 편성이 1건뿐이면
   * 고를 것이 없다. 채널 버튼과 같은 판단이다.
   */
  onOpenProducts: (() => void) | null;
  /**
   * v39. 시트가 「콘텐츠」 2단계인가(`contentSheetMode === 'series'`). 그때만 이름이 「콘텐츠」이고 아니면 「전체 회차」
   * 그대로다 — 라이브러리를 올리지 않은 채널의 바는 한 글자도 안 바뀐다(D8).
   */
  contentLevels?: boolean;
  /**
   * 구매 버튼. **선택 뒤 여기로 포커스가 옮겨진다** (절대 규칙 35) — 고른 다음 조작
   * 한 번(클릭·Enter·리모컨 OK)이 곧 구매다.
   *
   * `querySelector` 로 찾지 않는 이유는 **셀렉터가 화면 밖에 생기면 조용히 낡기**
   * 때문이다. 버튼이 여기 있는 한 이 ref 는 따라간다.
   */
  purchaseRef?: RefObject<HTMLButtonElement | null>;
  /**
   * 「전체 회차」 버튼 (v16). **일반 채널에서 선택 뒤 포커스가 여기로 온다** —
   * 그 채널에는 구매 버튼이 없다 (절대 규칙 35 의 예외).
   *
   * `focusPurchase()` 를 재사용할 수 없다. 그 함수는 `purchaseRef` 를 rAF 로 폴링하는데
   * 일반 채널에서는 그 ref 가 영원히 `null` 이라 **30프레임을 헛돌고 포커스가 사라진다.**
   */
  allProductsRef?: RefObject<HTMLButtonElement | null>;
  /** 핸드오프 결과 오버레이·시트가 떠 있는가 */
  handoffOpen: boolean;
  /** 트리거 요청 중인가. **연타를 막는다** */
  handoffPending: boolean;
  infoOpen: boolean;
  skip: SkipButtonState;
  /**
   * v21. **광고 중인가.** `null` 이면 지금까지와 한 픽셀도 다르지 않다.
   *
   * ★ **모드가 둘이 아니라 셋이 됐다** — 홈쇼핑 / 일반 / **광고**. boolean 둘로 두면
   * 「일반 채널의 광고」가 표현되지 않는다(둘 다 참인 상태가 생긴다).
   */
  adBreak: AdBreakView | null;
  /**
   * v33. **바이패스 중인가** (절대 규칙 83). `null` 이면 지금까지와 한 글자도 다르지 않다.
   *
   * 이 값 하나가 **없는 것 여섯**을 가른다 — 건너뛰기·전체 회차·구매·정보·광고 구매·
   * 광고 스킵. 시계는 `WatchScreen` 이 가른다(그쪽은 이 바 밖이다).
   */
  live: LiveView | null;
  /**
   * v34-b. **「라이브 ↔ 이어보기」** — 라이브 창이 열려 있어 두 스트림을 다 볼 수 있는 동안
   * 편성표를 열지 않고 오간다. `'live'` 면 라이브로(`joinLive`), `'recorded'` 면 컷 자리로
   * (`resumeRecorded` · 절대 규칙 84). **`null` 이면 렌더하지 않는다**(규칙 29·45 의 모양).
   */
  liveSwitch: LiveSwitchTarget | null;
  onSwitchLive: () => void;
  onRequestHandoff: () => void;
  onToggleInfo: () => void;
  onSkip: () => void;
  /** v32 — 「홈」. 뒤로 키가 누른다 (`data-focus-back`) */
  onHome: () => void;
  /** v21. **광고 스킵. 회차 스킵과 다른 핸들러다** (`docs/45` §8.5) */
  onSkipAd: () => void;
  /** v21. 광고 구매. **`purchase` 를 재사용하지 않는다** — 아래 주석 */
  onRequestAdHandoff: () => void;
  /**
   * v35. **녹화물 탐색·정지** (`docs/71` · 절대 규칙 90). **`null` 이면 셋 다 그리지 않는다** —
   * 라이브 바이패스·광고·재생 전이다(`disabled` 가 아니다 · 규칙 29·45). 광고와 라이브는 이 바가
   * 한 번 더 막는다(아래 `inAd`·`inLive`) — 둘이 갈리는 날 버튼이 라이브 위에 남는 것보다 낫다.
   */
  transport: {
    paused: boolean;
    onTogglePause: () => void;
    onSeek: (deltaSec: number) => void;
  } | null;
}) {
  const t = useT();
  /*
   * v33. **바이패스 중에는 없는 것이 여섯이다** (절대 규칙 85 · `docs/67` §8.2).
   *
   * ★ **`disabled` 가 아니라 렌더하지 않는다** — 포커스 후보가
   * `button:not([disabled]), a[href]` 라 비활성도 링에서 빠지지만, **없는 것이 더
   * 정직하다**: 라이브 중에 「건너뛰기」가 회색으로 있으면 시청자는 자기가 뭘 잘못한
   * 줄 안다. 규칙 45 가 일반 채널의 구매 버튼에 대해 정한 그 모양이다.
   */
  const inLive = live !== null;
  // **광고와 라이브는 함께 참일 수 없다** — 진입이 pod 을 버린다(`docs/67` D6).
  // 그래도 한 줄로 묶어 둔다: 갈리는 날 광고 UI 가 라이브 위에 남는 것보다 낫다.
  const inAd = adBreak !== null && !inLive;
  // **`null` 을 홈쇼핑으로 보지 않는다.** 목록을 받기 전에 상거래 버튼을 그리면
  // 일반 채널에서 잠깐 보였다 사라진다.
  //
  // v21. **광고 중에는 회차의 상거래 표면을 그리지 않는다** — 지금 화면에 있는 것은
  // 그 상품이 아니다.
  const commerce = channelKind === 'homeshopping' && !inAd && !inLive;
  const isGeneral = channelKind === 'general';
  return (
    <div className="controls">
      {/*
        채널 전환 (v9, `docs/23` §7.4). **채널명을 눌러 목록 시트를 여는 형태**를 골랐다 —
        좌우 화살표는 채널이 늘면 어디쯤인지 알 수 없고, 무엇보다 **지금 무엇을 보고
        있는지가 화면에 없었다.**

        **재생을 멈추지 않는다** (절대 규칙 14). 시트가 열려도 영상은 뒤에서 계속 간다 —
        구매·정보와 같은 자리다. 채널을 실제로 **바꿀 때**는 리시버가 통째로 다시 서고,
        그것은 정지가 아니라 다른 채널을 트는 것이다 (절대 규칙 27).
      */}
      {onOpenChannels !== null && (
        /*
          아이콘 + **번호**. 이름은 `aria-label`·이름표로만 보인다 — 말줄임(…)이 사라진다.
          `data-i18n="data"` 를 그대로 둔다: 채널 이름은 사람이 넣은 값이라 영어 화면의 한글 훑기
          (`verify-locale` 은 `aria-label` 도 본다)가 그 표시로 비켜 간다.
        */
        <button
          type="button"
          className="controls__button controls__button--icon controls__button--channel"
          data-testid="channel-switch"
          data-i18n="data"
          aria-label={
            channelName === null
              ? t('channels.change')
              : channelNumber === null
                ? channelName
                : `${String(channelNumber)} ${channelName}`
          }
          onClick={onOpenChannels}
        >
          <PlayerIcon name="channel" />
          {channelNumber !== null && channelName !== null && (
            <span className="controls__number">{String(channelNumber)}</span>
          )}
        </button>
      )}
      {/*
        ★ v32 — **「홈」** (`docs/63` §6.4 · D15 · 절대 규칙 82). `data-focus-back` 이라
        **리모컨 뒤로 키가 이 버튼을 누른다**(규칙 30 의 그 예외 — 기본 층의 「닫기」다).
        v10 의 「시청 화면의 뒤로 키는 우리 것이 아니다」는 홈이 생기며 뒤집혔다(`docs/41`).
        **한 층에 하나여야 한다** — 시트가 열리면 그 층의 닫기가 먼저다(층 우선순위).
        채널 버튼 **뒤**에 둔다 — `entryFocusIndex` 는 DOM 순서의 가장 앞이라, 앞에 두면
        재생 뒤 첫 화살표가 「홈」에 착지하고 실수의 Enter 가 화면을 떠난다.
      */}
      <button
        type="button"
        className="controls__button controls__button--icon"
        data-testid="go-home"
        data-focus-back
        aria-label={t('watch.home')}
        onClick={onHome}
      >
        <PlayerIcon name="home" />
      </button>
      {/*
        채널 재핑 ▲▼ (v25). **채널 이름 버튼 바로 옆이다** — 그 버튼이 편성표를 열고,
        이 둘은 편성표를 열지 않고 **바로 옆 채널로 옮긴다.**

        ★ **네이티브 `<button>` 이다.** 포커스 후보가 `button:not([disabled]), a[href]`
        뿐이라 `<div onClick>` 이면 **리모컨으로 닿지 않는데 마우스로는 멀쩡히 눌리고**,
        개발 기기에서는 영원히 드러나지 않는다 (절대 규칙 29·65).

        ★★ **글자를 `aria-label` 로만 두지 않는다.** 화살표 하나는 3m 에서 방향이
        갈리지만 **무엇의 화살표인지는 안 갈린다** — 그래서 이름 버튼 **바로 옆**에
        붙여 놓는 것이 그 자리의 뜻이고, 접근 가능한 이름은 사전을 거친다(절대 규칙 24 —
        한글 훑기가 `aria-label` 도 본다).

        **광고 중에도 남긴다** — 채널을 바꾸는 것은 그 pod 을 버리는 일이지만, 그것은
        시청자가 고른 결과다. 채널 이름 버튼이 광고 중에도 남는 것과 같은 판단이다.
      */}
      {onZapChannel !== null && (
        <span className="controls__zap">
          <button
            type="button"
            className="controls__button controls__button--icon controls__button--zap"
            data-testid="channel-up"
            aria-label={t('watch.channelUp')}
            onClick={() => {
              onZapChannel(-1);
            }}
          >
            <PlayerIcon name="chUp" />
          </button>
          <button
            type="button"
            className="controls__button controls__button--icon controls__button--zap"
            data-testid="channel-down"
            aria-label={t('watch.channelDown')}
            onClick={() => {
              onZapChannel(1);
            }}
          >
            <PlayerIcon name="chDown" />
          </button>
        </span>
      )}
      {/*
        ★ v34-b. **「라이브 ↔ 이어보기」** — 편성표의 두 칸(`docs/67` §9.4)을 **열지 않고** 누르는
        자리다. 부르는 문도 그 둘 그대로다(`joinLive` · `resumeRecorded`) — 새 전환 경로가 아니다.

        **바이패스 중에도 산다** — 절대 규칙 85 가 남긴 넷 중 「녹화 상자 선택」의 다른 입구다.
        ▲▼ **뒤**에 둔다 — 라이브 중 바에는 이름 · 홈 · ▲▼ 와 이것뿐이라 오른쪽 끝의 한 번이다.
        DOM 순서가 곧 보이는 순서라 리모컨 링도 그대로 닿는다(규칙 29 — 등록할 것이 없다).
      */}
      {liveSwitch !== null && (
        <button
          type="button"
          className={`controls__button controls__button--live-switch${
            liveSwitch === 'live' ? ' is-live' : ''
          }`}
          data-testid="live-switch"
          data-live-switch={liveSwitch}
          onClick={onSwitchLive}
        >
          {liveSwitch === 'live' ? t('watch.toLive') : t('watch.toRecorded')}
        </button>
      )}
      {/*
        「전체 상품」 (v11, `docs/27`). 편성 전체를 조망하고 고르면 그 상품부터 재생된다 —
        **재생을 멈추지 않는다** (절대 규칙 14). 시트는 채널 시트와 같은 층(`.sheet`)이라
        영상을 축소하지 않고 덮고, 이 바는 그 스크림 뒤에 남는다 (2026-08-10 개정 —
        v11 에서는 영상이 위로 축소되고 `.stage--mini` 가 이 바를 숨겼다).

        **다섯째 버튼이다.** 넷이 되던 때 폰 폭에서 뷰포트 밖으로 밀린 적이 있어(v9 C9)
        하네스가 430px 에서 다섯이 전부 닿는지를 매 실행 검사한다
        (`select.fitsAtPhoneWidth`) — 1280px 하네스에서는 나오지 않는 결함이다.
      */}
      {onOpenProducts !== null && !inAd && !inLive && (
        <button
          ref={allProductsRef}
          type="button"
          className="controls__button controls__button--icon"
          data-testid="all-products"
          aria-label={
            isGeneral
              ? contentLevels
                ? t('watch.allContents')
                : t('watch.allEpisodes')
              : t('watch.allProducts')
          }
          onClick={onOpenProducts}
        >
          <PlayerIcon name="episodes" />
        </button>
      )}
      {/*
        요청 중에는 비활성이다. 트리거는 서버 왕복이라 즉시 답하지 않는데, 그동안 두 번
        누르면 앞의 응답이 뒤늦게 도착해 화면이 두 번 바뀐다 (서버의 3초 디바운스가 덮는
        것은 같은 상품의 연타뿐이고, 화면 상태까지 돌봐 주지는 않는다).

        **선택 뒤 포커스가 여기로 온다** (절대 규칙 35). 누르지는 않는다 — `click()` 을
        부르면 시청자의 Enter 와 겹쳐 `purchase_click` 이 두 건이 되고, 그 증상은 화면이
        아니라 분석에서만 갈린다.
      */}
      {commerce && (
        <button
          ref={purchaseRef}
          type="button"
          className={`controls__button${handoffOpen ? ' is-on' : ''}`}
          data-testid="purchase"
          onClick={onRequestHandoff}
          disabled={handoffPending}
          aria-pressed={handoffOpen}
        >
          {handoffPending ? t('watch.requesting') : t(purchaseLabelKey(clientType))}
        </button>
      )}
      {commerce && (
        <button
          type="button"
          className={`controls__button${infoOpen ? ' is-on' : ''}`}
          data-testid="info"
          onClick={onToggleInfo}
          aria-pressed={infoOpen}
        >
          {t('watch.info')}
        </button>
      )}

      {/* v21. 광고 중의 구매 — v38 에 `AdControls` 로 꺼냈다(ONAIR CSPM 이 같이 쓴다 · 주석은 그쪽에) */}
      {inAd && (
        <AdPurchaseButton
          handoffOpen={handoffOpen}
          handoffPending={handoffPending}
          onClick={onRequestAdHandoff}
        />
      )}

      {/*
        `skip.allowed === false` 면 버튼을 **숨긴다.** 비활성으로 두면 "조금 기다리면
        누를 수 있다"는 잘못된 신호가 된다 — SDK 의 `secondsUntilSkippable()` 이 그 상태에
        0 이 아니라 null 을 주는 이유와 같다.

        v21. **광고 중에는 광고 스킵이다** — 다른 `testid`·다른 핸들러·다른 문구.
        하나로 겸하면 `send('skip')` 이 **회차 이탈률을 광고 스킵으로 부풀린다.**
      */}
      {/* v33. **라이브는 건너뛸 수 없다** — 코어도 막지만(절대 규칙 85) 버튼부터 없다 */}
      {inLive
        ? null
        : inAd
          ? <AdSkipButton skip={skip} onClick={onSkipAd} />
          : skip.visible && (
              /*
                아이콘 + **남은 초**(비활성일 때만). 숫자를 지우면 「왜 안 눌리는가」가 화면에서
                사라진다 — 이름표는 포커스가 있어야 뜨고 비활성 버튼은 포커스를 받지 않는다.
                문장은 `aria-label` 이 그대로 말한다(`watch.skipIn`).
              */
              <button
                type="button"
                className="controls__button controls__button--icon controls__button--skip"
                data-testid="skip"
                aria-label={
                  skip.enabled
                    ? isGeneral
                      ? t('watch.nextEpisode')
                      : t('watch.skip')
                    : t('watch.skipIn', { n: skip.secondsLeft })
                }
                onClick={onSkip}
                disabled={!skip.enabled}
              >
                <PlayerIcon name="nextEpisode" />
                {!skip.enabled && (
                  <span className="controls__number">{String(skip.secondsLeft)}</span>
                )}
              </button>
            )}
      {/*
        ★ v35. **「10초 전 · 일시정지/재생 · 10초 후」** (`docs/71` §3 · 절대 규칙 90~92).

        **바의 끝에 둔다** — 진입 포커스는 DOM 순서의 가장 앞(`entryFocusIndex`)이라, 앞에 두면
        재생 뒤 첫 화살표가 「10초 전」에 착지하고 실수의 Enter 가 영상을 되감는다. 문은 리시버
        하나이고 **컷 앞에서 서는 판정은 코어가 한다**(`seekTarget`) — 그래서 「10초 후」는 컷
        앞에서도 비활성이 아니다: 누르면 제자리일 뿐이고, 비활성으로 바꾸면 1초마다 깜박인다.

        **정지 버튼만 `aria-pressed`** 다 — 켜고 끄는 것이 그것 하나다. 체크박스로 만들지 않는다
        (포커스 후보가 `button`·`a[href]` 뿐이다).
      */}
      {transport !== null && !inAd && !inLive && (
        <span className="controls__transport">
          <button
            type="button"
            className="controls__button controls__button--icon controls__button--transport"
            data-testid="seek-back"
            aria-label={t('watch.back10')}
            onClick={() => {
              transport.onSeek(-SEEK_STEP_SEC);
            }}
          >
            <PlayerIcon name="back10" />
          </button>
          <button
            type="button"
            className="controls__button controls__button--icon controls__button--transport controls__button--primary"
            data-testid="play-pause"
            data-paused={transport.paused ? 'true' : 'false'}
            aria-pressed={transport.paused}
            aria-label={transport.paused ? t('watch.resume') : t('watch.pause')}
            onClick={transport.onTogglePause}
          >
            <PlayerIcon name={transport.paused ? 'play' : 'pause'} />
          </button>
          <button
            type="button"
            className="controls__button controls__button--icon controls__button--transport"
            data-testid="seek-forward"
            aria-label={t('watch.forward10')}
            onClick={() => {
              transport.onSeek(SEEK_STEP_SEC);
            }}
          >
            <PlayerIcon name="fwd10" />
          </button>
        </span>
      )}
    </div>
  );
}
